import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../../engine/api/context';
import { useAction, useMeta } from '../../engine/api/hooks';
import { fieldOf } from '../../engine/meta/types';
import { Button } from '../../engine/design/primitives/Button';
import { Dialog } from '../../engine/design/primitives/Overlay';
import { Field, Input } from '../../engine/design/primitives/Input';
import { Combobox } from '../../engine/design/primitives/Combobox';
import { DatePicker } from '../../engine/design/primitives/DatePicker';
import { RelationPicker } from '../../engine/fields/RelationPicker';
import { MoneyEditor } from '../../engine/fields/registry';
import type { FieldMeta } from '../../engine/meta/types';
import { todayIso } from './data';

// "New from template" on the projects list: pick a template, name the project, link the
// client, set the start date and an optional total budget → POST /projects/from-template →
// open the new project. `?template=1` opens it on arrival (⌘K).

const budgetField: FieldMeta = { name: 'budget_minor', label: 'Budget', type: 'money', required: false, readOnly: false, nullable: true, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: true, inList: true, inDetail: true };

export function FromTemplateAction() {
  const api = useApi();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const meta = useMeta('projects').data;
  const [open, setOpen] = useState(() => params.has('template'));
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [start, setStart] = useState<string | null>(todayIso());
  const [budget, setBudget] = useState<number | null>(null);
  const create = useAction<{ project: { id: string } }>('projects', 'from-template', {
    toast: { success: 'Project created' },
    invalidate: ['project-tasks'],
    onSuccess: (r) => {
      close();
      navigate(`/projects/${r.project.id}`);
    },
  });
  const close = () => {
    setOpen(false);
    if (params.has('template')) {
      const next = new URLSearchParams(params);
      next.delete('template');
      setParams(next, { replace: true });
    }
  };
  const clientField = meta ? fieldOf(meta, 'client_id') : undefined;
  const loadTemplates = async (q: string) => {
    const page = await api.resource('project-templates').list({ q: q || undefined, limit: 20, sort: 'name', filter: [{ field: 'is_active', op: 'eq', value: true }] });
    return page.data.map((t) => ({ value: String(t.id), label: String(t.name), sub: t.description ? String(t.description) : undefined }));
  };
  const valid = Boolean(templateId && name.trim());
  return (
    <>
      <Button icon="file-text" onClick={() => setOpen(true)}>
        New from template
      </Button>
      <Dialog open={open} onClose={close} title="New project from template" description="Phases, tasks and dependencies come from the template; deferred tasks appear as their predecessors finish." locked={create.isPending}>
        <form
          className="hf-stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            create.mutate({ body: { template_id: templateId, name: name.trim(), client_id: clientId, start_date: start, budget_minor: budget } });
          }}
        >
          <Field label="Template" required>
            <Combobox<string> value={templateId} onChange={(v) => setTemplateId(v)} loadOptions={loadTemplates} placeholder="Search templates…" autoFocus />
          </Field>
          <Field label="Project name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme onboarding" />
          </Field>
          {clientField && (
            <Field label="Client">
              <RelationPicker field={clientField} value={clientId} onChange={(v) => setClientId(v)} />
            </Field>
          )}
          <div className="hf-row" style={{ alignItems: 'flex-start' }}>
            <Field label="Start date">
              <DatePicker value={start} onChange={setStart} />
            </Field>
            <Field label="Total budget">
              <MoneyEditor field={budgetField} value={budget} onChange={(v) => setBudget(v == null ? null : Number(v))} currency="USD" />
            </Field>
          </div>
          <div className="hf-form-actions">
            <Button variant="ghost" onClick={close} disabled={create.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" busy={create.isPending} disabled={!valid}>
              Create project
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
